import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IoCloseSharp } from 'react-icons/io5';
import { appendBatteryNodeToPipeline, editBatteryNode, getGlobalParams } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './BatteryNode.css';

const numberOrEmpty = (value) => (value === undefined || value === null ? '' : value);
const valueOrDefault = (value, fallback) => (
  value === undefined || value === null || value === '' ? fallback : value
);
const BATTERY_PROFILE_YEARS = 25;
// Default SAT → Year 25 SOH / DC RTE until the user edits them.
const DEFAULT_ANNUAL_PROFILE_VALUES = [
  { soh_pct: 97, dc_rte_pct: 94.75 },
  { soh_pct: 94.43, dc_rte_pct: 94.65 },
  { soh_pct: 91.49, dc_rte_pct: 94.55 },
  { soh_pct: 89.46, dc_rte_pct: 94.45 },
  { soh_pct: 87.57, dc_rte_pct: 94.35 },
  { soh_pct: 86.35, dc_rte_pct: 94.25 },
  { soh_pct: 84.55, dc_rte_pct: 94.15 },
  { soh_pct: 83.42, dc_rte_pct: 94.05 },
  { soh_pct: 81.85, dc_rte_pct: 93.95 },
  { soh_pct: 80.67, dc_rte_pct: 93.85 },
  { soh_pct: 78.96, dc_rte_pct: 93.76 },
  { soh_pct: 76.85, dc_rte_pct: 93.67 },
  { soh_pct: 76.35, dc_rte_pct: 93.58 },
  { soh_pct: 75.24, dc_rte_pct: 93.49 },
  { soh_pct: 74.14, dc_rte_pct: 93.4 },
  { soh_pct: 72.66, dc_rte_pct: 93.31 },
  { soh_pct: 71.56, dc_rte_pct: 93.23 },
  { soh_pct: 70.47, dc_rte_pct: 93.15 },
  { soh_pct: 69.37, dc_rte_pct: 93.07 },
  { soh_pct: 67.92, dc_rte_pct: 92.99 },
  { soh_pct: 66.83, dc_rte_pct: 92.91 },
  { soh_pct: 65.74, dc_rte_pct: 92.83 },
  { soh_pct: 64.65, dc_rte_pct: 92.75 },
  { soh_pct: 63.55, dc_rte_pct: 92.67 },
  { soh_pct: 62.46, dc_rte_pct: 92.59 },
  { soh_pct: 61.37, dc_rte_pct: 92.59 },
];
const createDefaultBatteryProfile = () => Array.from(
  { length: BATTERY_PROFILE_YEARS + 1 },
  (_, index) => ({
    period: index === 0 ? 'SAT' : String(index),
    soh_pct: DEFAULT_ANNUAL_PROFILE_VALUES[index].soh_pct,
    dc_rte_pct: DEFAULT_ANNUAL_PROFILE_VALUES[index].dc_rte_pct,
  })
);
const normalizeBatteryProfile = (profile) => {
  const defaults = createDefaultBatteryProfile();
  if (!Array.isArray(profile) || profile.length === 0) return defaults;
  return defaults.map((row, index) => {
    const saved = profile[index];
    const hasSavedSoh = saved && saved.soh_pct !== undefined && saved.soh_pct !== null && saved.soh_pct !== '';
    const hasSavedRte = saved && saved.dc_rte_pct !== undefined && saved.dc_rte_pct !== null && saved.dc_rte_pct !== '';
    return {
      ...row,
      soh_pct: hasSavedSoh ? saved.soh_pct : row.soh_pct,
      dc_rte_pct: hasSavedRte ? saved.dc_rte_pct : row.dc_rte_pct,
    };
  });
};

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const hourLabel = (hour) => {
  const endHour = hour === 23 ? 24 : hour + 1;
  return `${String(hour).padStart(2, '0')}:00-${String(endHour).padStart(2, '0')}:00`;
};

const TimeSlotPicker = ({ label, tone, selected, onChange, unavailable = [] }) => {
  const dragActionRef = useRef(null);
  const unavailableSet = new Set(unavailable);
  const selectedSet = new Set(selected);

  useEffect(() => {
    const stopDragging = () => { dragActionRef.current = null; };
    document.addEventListener('pointerup', stopDragging);
    document.addEventListener('pointercancel', stopDragging);
    return () => {
      document.removeEventListener('pointerup', stopDragging);
      document.removeEventListener('pointercancel', stopDragging);
    };
  }, []);

  const updateHour = (hour, shouldSelect) => {
    const isSelected = selectedSet.has(hour);
    if (unavailableSet.has(hour) || shouldSelect === isSelected) return;
    const next = shouldSelect
      ? [...selected, hour]
      : selected.filter((selectedHour) => selectedHour !== hour);
    onChange(next.sort((a, b) => a - b));
  };

  const beginSelection = (event, hour) => {
    event.preventDefault();
    if (unavailableSet.has(hour)) return;
    const shouldSelect = !selectedSet.has(hour);
    dragActionRef.current = shouldSelect;
    updateHour(hour, shouldSelect);
  };

  return (
    <section className={`battery-time-picker ${tone}`} aria-label={`${label} time slots`}>
      <div className="battery-time-picker-header">
        <div>
          <span className="battery-time-picker-label">{label}</span>
          <strong>{selected.length} {selected.length === 1 ? 'hour' : 'hours'}</strong>
        </div>
        <span className="battery-time-picker-range">
          {selected.length ? selected.map((hour) => String(hour).padStart(2, '0')).join(', ') : 'No slots selected'}
        </span>
      </div>
      <div className="battery-time-slot-rail">
        {HOURS.map((hour) => {
          const isSelected = selectedSet.has(hour);
          const isUnavailable = unavailableSet.has(hour);
          return (
            <button
              key={hour}
              type="button"
              className={`battery-time-slot ${isSelected ? 'selected' : ''}`}
              aria-label={`${label} ${hourLabel(hour)}`}
              aria-pressed={isSelected}
              disabled={isUnavailable}
              title={hourLabel(hour)}
              onPointerDown={(event) => beginSelection(event, hour)}
              onPointerEnter={() => {
                if (dragActionRef.current !== null) updateHour(hour, dragActionRef.current);
              }}
              onClick={(event) => {
                if (event.detail === 0) updateHour(hour, !isSelected);
              }}
            >
              {String(hour).padStart(2, '0')}
            </button>
          );
        })}
      </div>
      <div className="battery-time-axis" aria-hidden="true">
        <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
      </div>
    </section>
  );
};

const BatteryConfigModal = ({ isOpen, onClose, onConfigured, initialData = {} }) => {
  const { session } = usePipeline();
  const [name, setName] = useState('');
  const [nodeNameError, setNodeNameError] = useState('');
  const [genColName, setGenColName] = useState('');
  const [batteryHours, setBatteryHours] = useState(2);
  const [chargeTimeSlots, setChargeTimeSlots] = useState([]);
  const [dischargeTimeSlots, setDischargeTimeSlots] = useState([]);
  const [batteryCapacityKw, setBatteryCapacityKw] = useState('');
  const [roundTripEfficiency, setRoundTripEfficiency] = useState(90);
  const [dod, setDod] = useState(90);
  const [batteryCapexPerKwh, setBatteryCapexPerKwh] = useState('');
  const [batteryOmPerc, setBatteryOmPerc] = useState(2);
  const [loanPerc, setLoanPerc] = useState(80);
  const [annualRate, setAnnualRate] = useState(9);
  const [tenureYears, setTenureYears] = useState(9);
  const [depRateForITWDV, setDepRateForITWDV] = useState(40);
  const [addnDepRate, setAddnDepRate] = useState(0);
  const [taxRate, setTaxRate] = useState(25);
  const [gstInput, setGstInput] = useState(false);
  const [gstPerc, setGstPerc] = useState(0);
  const [activeTab, setActiveTab] = useState('configuration');
  const [assumptionsEnabled, setAssumptionsEnabled] = useState(true);
  const [calendarRetentionPct, setCalendarRetentionPct] = useState(100);
  const [acEfficiencyPct, setAcEfficiencyPct] = useState(96.16);
  const [auxiliaryLossMwh, setAuxiliaryLossMwh] = useState(0.088);
  const [legacyAuxiliaryLoadKw, setLegacyAuxiliaryLoadKw] = useState(null);
  const [cyclesPerDay, setCyclesPerDay] = useState(1);
  const [maximumCycleLife, setMaximumCycleLife] = useState(8000);
  const [annualProfile, setAnnualProfile] = useState(createDefaultBatteryProfile);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initializedOpenKeyRef = useRef(null);

  const isEditing = !!initialData.name && initialData.isConfigured === true;

  useEffect(() => {
    if (!isOpen) {
      initializedOpenKeyRef.current = null;
      return;
    }
    const openKey = `${initialData.name || ''}:${initialData.isConfigured === true ? 'edit' : 'create'}`;
    if (initializedOpenKeyRef.current === openKey) return;
    initializedOpenKeyRef.current = openKey;

    setName(initialData.name || '');
    setGenColName(initialData.genColName || '');
    setBatteryHours(valueOrDefault(initialData.batteryHours, 2));
    setChargeTimeSlots(Array.isArray(initialData.chargeTimeSlots) ? initialData.chargeTimeSlots : []);
    setDischargeTimeSlots(Array.isArray(initialData.dischargeTimeSlots) ? initialData.dischargeTimeSlots : []);
    setBatteryCapacityKw(numberOrEmpty(initialData.batteryCapacityKw));
    setRoundTripEfficiency(valueOrDefault(initialData.roundTripEfficiency, 90));
    setDod(valueOrDefault(initialData.dod, 90));
    setBatteryCapexPerKwh(numberOrEmpty(initialData.batteryCapexPerKwh));
    setBatteryOmPerc(valueOrDefault(initialData.batteryOmPerc, 2));
    setLoanPerc(valueOrDefault(initialData.loanPerc, 80));
    setAnnualRate(valueOrDefault(initialData.annualRate, 9));
    setTenureYears(valueOrDefault(initialData.tenureYears, 9));
    setDepRateForITWDV(valueOrDefault(initialData.depRateForITWDV, 40));
    setAddnDepRate(valueOrDefault(initialData.addnDepRate, 0));
    setTaxRate(valueOrDefault(initialData.taxRate, 25));
    setGstInput(Boolean(initialData.gstInput));
    setGstPerc(valueOrDefault(initialData.gstPerc, 0));
    const savedAssumptions = initialData.batteryAssumptions || {};
    setAssumptionsEnabled(isEditing ? Boolean(savedAssumptions.enabled) : true);
    setCalendarRetentionPct(valueOrDefault(savedAssumptions.calendar_retention_pct, 100));
    setAcEfficiencyPct(valueOrDefault(savedAssumptions.ac_efficiency_pct, 96.16));
    const savedAuxiliaryLoss = savedAssumptions.auxiliary_loss_mwh;
    const savedLegacyAuxiliaryLoad = savedAssumptions.auxiliary_load_kw;
    setAuxiliaryLossMwh(
      savedAuxiliaryLoss !== undefined && savedAuxiliaryLoss !== null
        ? savedAuxiliaryLoss
        : (savedLegacyAuxiliaryLoad !== undefined && savedLegacyAuxiliaryLoad !== null ? '' : 0.088)
    );
    setLegacyAuxiliaryLoadKw(
      savedAuxiliaryLoss === undefined || savedAuxiliaryLoss === null
        ? (savedLegacyAuxiliaryLoad ?? null)
        : null
    );
    setCyclesPerDay(valueOrDefault(savedAssumptions.cycles_per_day, 1));
    setMaximumCycleLife(valueOrDefault(savedAssumptions.maximum_cycle_life, 8000));
    setAnnualProfile(normalizeBatteryProfile(savedAssumptions.annual_profile));
    setActiveTab('configuration');
    setError(null);
  }, [isOpen, initialData, isEditing]);

  useEffect(() => {
    const validation = validateNodeName(name);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [name]);

  useEffect(() => {
    const loadGlobalDefaults = async () => {
      if (!isOpen || isEditing || !session?.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') return;
      try {
        const gp = await getGlobalParams(session.pipeline_name);
        if (gp?.loan_perc != null) setLoanPerc(gp.loan_perc);
        if (gp?.annual_rate != null) setAnnualRate(gp.annual_rate);
        if (gp?.tenure_years != null) setTenureYears(gp.tenure_years);
        if (gp?.depreciation_rate != null) setDepRateForITWDV(gp.depreciation_rate);
        if (gp?.addn_dep_rate != null) setAddnDepRate(gp.addn_dep_rate);
        if (gp?.tax_rate != null) setTaxRate(gp.tax_rate);
        if (gp?.gst_perc != null) setGstPerc(gp.gst_perc);
      } catch (_error) {
        // Defaults are only a convenience.
      }
    };
    loadGlobalDefaults();
  }, [isOpen, isEditing, session?.pipeline_name]);

  if (!isOpen) return null;

  const toFloat = (value, label) => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) throw new Error(`Please enter a valid ${label}.`);
    return parsed;
  };

  const toOptionalFloat = (value, label) => {
    if (value === undefined || value === null || String(value).trim() === '') return null;
    return toFloat(value, label);
  };

  const updateProfileCell = (index, field, value) => {
    setAnnualProfile((current) => current.map(
      (row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)
    ));
  };

  const buildBatteryAssumptions = () => {
    if (!assumptionsEnabled) return { enabled: false };
    const profile = annualProfile.map((row) => ({
      period: row.period,
      soh_pct: toFloat(row.soh_pct, `SOH for ${row.period}`),
      dc_rte_pct: toFloat(row.dc_rte_pct, `DC RTE for ${row.period}`),
    }));
    profile.forEach((row) => {
      if (row.soh_pct < 0 || row.soh_pct > 100) {
        throw new Error(`SOH for ${row.period} must be between 0 and 100%.`);
      }
      if (row.dc_rte_pct <= 0 || row.dc_rte_pct > 100) {
        throw new Error(`DC RTE for ${row.period} must be greater than 0 and at most 100%.`);
      }
    });

    const assumptions = {
      enabled: true,
      calendar_retention_pct: toFloat(calendarRetentionPct, 'calendar retention'),
      ac_efficiency_pct: toFloat(acEfficiencyPct, 'AC efficiency at POI'),
      cycles_per_day: toFloat(cyclesPerDay, 'cycles per day'),
      maximum_cycle_life: toFloat(maximumCycleLife, 'maximum cycle life'),
      annual_profile: profile,
    };
    if (auxiliaryLossMwh !== '') {
      assumptions.auxiliary_loss_mwh = toFloat(auxiliaryLossMwh, 'auxiliary losses');
    } else if (legacyAuxiliaryLoadKw !== null) {
      assumptions.auxiliary_load_kw = legacyAuxiliaryLoadKw;
    } else {
      throw new Error('Please enter valid auxiliary losses in MWh.');
    }
    if (assumptions.calendar_retention_pct <= 0 || assumptions.calendar_retention_pct > 100) {
      throw new Error('Calendar retention must be greater than 0 and at most 100%.');
    }
    if (assumptions.ac_efficiency_pct <= 0 || assumptions.ac_efficiency_pct > 100) {
      throw new Error('AC efficiency at POI must be greater than 0 and at most 100%.');
    }
    if (
      assumptions.auxiliary_loss_mwh !== undefined
      && assumptions.auxiliary_loss_mwh < 0
    ) {
      throw new Error('Auxiliary losses cannot be negative.');
    }
    if (assumptions.cycles_per_day <= 0) {
      throw new Error('Cycles per day must be greater than 0.');
    }
    if (assumptions.maximum_cycle_life < 0) {
      throw new Error('Maximum cycle life cannot be negative.');
    }
    return assumptions;
  };

  const profileIncreaseCount = annualProfile.reduce((count, row, index) => {
    if (index === 0) return count;
    const previous = annualProfile[index - 1];
    if (
      row.soh_pct === '' || row.dc_rte_pct === ''
      || previous.soh_pct === '' || previous.dc_rte_pct === ''
    ) return count;
    const sohIncreased = Number(row.soh_pct) > Number(previous.soh_pct);
    const rteIncreased = Number(row.dc_rte_pct) > Number(previous.dc_rte_pct);
    return count + (sohIncreased || rteIncreased ? 1 : 0);
  }, 0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    const validation = validateNodeName(name);
    if (!validation.isValid) {
      setNodeNameError(validation.errorMessage);
      return;
    }
    if (!initialData.inputNodeName) {
      setError('Connect a generation input before configuring the battery.');
      return;
    }

    try {
      setIsSubmitting(true);
      const batteryAssumptions = buildBatteryAssumptions();
      const parsedBatteryHours = toFloat(batteryHours, 'battery hours');
      if (!chargeTimeSlots.length || !dischargeTimeSlots.length) {
        throw new Error('Select at least one charging and one discharging time slot.');
      }
      if (chargeTimeSlots.some((hour) => dischargeTimeSlots.includes(hour))) {
        throw new Error('Charging and discharging time slots cannot overlap.');
      }
      const params = {
        genColName,
        batteryHours: parsedBatteryHours,
        chargeTimeSlots,
        dischargeTimeSlots,
        batteryCapacityKw: toOptionalFloat(batteryCapacityKw, 'battery capacity'),
        roundTripEfficiency: batteryAssumptions.enabled
          ? batteryAssumptions.annual_profile[0].dc_rte_pct
          : toFloat(roundTripEfficiency, 'round trip efficiency'),
        dod: toFloat(dod, 'DoD'),
        batteryCapexPerKwh: toFloat(batteryCapexPerKwh, 'battery CAPEX'),
        batteryOmPerc: toFloat(batteryOmPerc, 'battery O&M percentage'),
        loanPerc: toFloat(loanPerc, 'loan percentage'),
        annualRate: toFloat(annualRate, 'interest rate'),
        tenureYears: parseInt(tenureYears, 10),
        depRateForITWDV: toFloat(depRateForITWDV, 'depreciation rate'),
        addnDepRate: toFloat(addnDepRate, 'additional depreciation rate'),
        taxRate: toFloat(taxRate, 'tax rate'),
        gstInput,
        gstPerc: toFloat(gstPerc, 'GST percentage'),
        batteryAssumptions,
      };
      if (!Number.isFinite(params.tenureYears) || params.tenureYears <= 0) {
        throw new Error('Please enter a valid tenure in years.');
      }
      if (params.batteryCapacityKw !== null && params.batteryCapacityKw <= 0) {
        throw new Error('Battery capacity must be greater than 0 kW, or leave it blank to auto-calculate.');
      }

      const position = initialData.position || { x: 0, y: 0 };
      if (session.pipeline_name !== 'Unsaved_Pipeline') {
        if (isEditing) {
          await editBatteryNode(session.pipeline_name, initialData.name, params);
        } else {
          await appendBatteryNodeToPipeline(
            session.pipeline_name,
            name,
            initialData.inputNodeName,
            params,
            position.x,
            position.y
          );
        }
      }

      onConfigured({
        name: isEditing ? initialData.name : name,
        inputNodeName: initialData.inputNodeName,
        ...params,
      });
    } catch (submitError) {
      if (/time slot/i.test(submitError?.message || '')) {
        setActiveTab('configuration');
      } else if (/SOH|DC RTE|calendar retention|AC efficiency|cycle|auxiliary/i.test(submitError?.message || '')) {
        setActiveTab('assumptions');
      }
      const info = notifyError(submitError, {
        operation: isEditing ? OP.node.editBattery : OP.node.appendBattery,
        showToast: false,
      });
      setError(info?.message || submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="battery-modal-overlay">
      <form className="battery-modal-content" onSubmit={handleSubmit}>
        <button type="button" onClick={onClose} className="battery-modal-close" aria-label="Close">
          <IoCloseSharp size={20} />
        </button>
        <h2 className="battery-modal-title">{isEditing ? 'Edit Battery Node' : 'Configure Battery Node'}</h2>

        {error && <div className="battery-modal-error">{error}</div>}

        {!isEditing ? (
          <label className="battery-modal-label">
            Node Name
            <input className="battery-modal-input" value={name} onChange={(e) => setName(e.target.value)} />
            {nodeNameError && <span className="battery-modal-help">{nodeNameError}</span>}
          </label>
        ) : (
          <div className="battery-modal-name-display">Node Name: {name}</div>
        )}

        <div className="battery-modal-tabs" role="tablist" aria-label="Battery configuration sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'configuration'}
            className={`battery-modal-tab ${activeTab === 'configuration' ? 'active' : ''}`}
            onClick={() => setActiveTab('configuration')}
          >
            Configuration
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'assumptions'}
            className={`battery-modal-tab ${activeTab === 'assumptions' ? 'active' : ''}`}
            onClick={() => setActiveTab('assumptions')}
          >
            Battery assumptions
            <span className={`battery-tab-status ${assumptionsEnabled ? 'enabled' : 'legacy'}`}>
              {assumptionsEnabled ? 'Active' : 'Legacy'}
            </span>
          </button>
        </div>

        {activeTab === 'configuration' && (
          <div className="battery-modal-panel" role="tabpanel">
            <label className="battery-modal-label">
              Generation Column
              <input className="battery-modal-input" value={genColName} onChange={(e) => setGenColName(e.target.value)} placeholder="Leave blank to auto-pick" />
            </label>

            <div className="battery-modal-grid">
              <label className="battery-modal-label">
                Battery Hours
                <select className="battery-modal-input" value={batteryHours} onChange={(e) => setBatteryHours(e.target.value)}>
                  <option value="2">2</option>
                  <option value="4">4</option>
                  <option value="6">6</option>
                </select>
              </label>
              <label className="battery-modal-label">
                Battery Capacity (kW)
                <input
                  className="battery-modal-input"
                  type="number"
                  min="0"
                  step="any"
                  value={batteryCapacityKw}
                  onChange={(e) => setBatteryCapacityKw(e.target.value)}
                  placeholder="Auto-calculated"
                />
                <small className="battery-modal-help">Leave blank to auto-calculate.</small>
              </label>
              {!assumptionsEnabled ? (
                <label className="battery-modal-label">
                  Legacy Round Trip Efficiency (%)
                  <input className="battery-modal-input" type="number" value={roundTripEfficiency} onChange={(e) => setRoundTripEfficiency(e.target.value)} />
                </label>
              ) : (
                <div className="battery-derived-field">
                  <span>Round Trip Efficiency</span>
                  <strong>Defined by SAT DC RTE</strong>
                  <small>Manage this value in Battery assumptions.</small>
                </div>
              )}
              <label className="battery-modal-label">
                DoD (%)
                <input className="battery-modal-input" type="number" value={dod} onChange={(e) => setDod(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Battery CAPEX (Rs/kWh)
                <input className="battery-modal-input" type="number" value={batteryCapexPerKwh} onChange={(e) => setBatteryCapexPerKwh(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Battery O&M (% of CAPEX)
                <input className="battery-modal-input" type="number" value={batteryOmPerc} onChange={(e) => setBatteryOmPerc(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Loan (%)
                <input className="battery-modal-input" type="number" value={loanPerc} onChange={(e) => setLoanPerc(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Interest Rate (%)
                <input className="battery-modal-input" type="number" value={annualRate} onChange={(e) => setAnnualRate(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Tenure (years)
                <input className="battery-modal-input" type="number" value={tenureYears} onChange={(e) => setTenureYears(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Depreciation WDV (%)
                <input className="battery-modal-input" type="number" value={depRateForITWDV} onChange={(e) => setDepRateForITWDV(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Additional Depreciation (%)
                <input className="battery-modal-input" type="number" value={addnDepRate} onChange={(e) => setAddnDepRate(e.target.value)} />
              </label>
              <label className="battery-modal-label">
                Tax Rate (%)
                <input className="battery-modal-input" type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
              </label>
              <label className="battery-modal-label battery-checkbox-label">
                <input type="checkbox" checked={gstInput} onChange={(e) => setGstInput(e.target.checked)} />
                GST input credit
              </label>
              <label className="battery-modal-label">
                GST (%)
                <input className="battery-modal-input" type="number" value={gstPerc} onChange={(e) => setGstPerc(e.target.value)} />
              </label>
            </div>

            <div className="battery-schedule-editor">
              <TimeSlotPicker
                label="Charging window"
                tone="charge"
                selected={chargeTimeSlots}
                onChange={setChargeTimeSlots}
                unavailable={dischargeTimeSlots}
              />
              <TimeSlotPicker
                label="Discharging window"
                tone="discharge"
                selected={dischargeTimeSlots}
                onChange={setDischargeTimeSlots}
                unavailable={chargeTimeSlots}
              />
              {(!chargeTimeSlots.length || !dischargeTimeSlots.length) && (
                <div className="battery-schedule-status" role="status">
                  Select at least one charging slot and one discharging slot to save this node.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'assumptions' && (
          <div className="battery-modal-panel battery-assumptions-panel" role="tabpanel">
            <div className="battery-assumptions-intro">
              <div>
                <span className="battery-assumptions-kicker">Yearwise performance model</span>
                <h3>SAT baseline → Year 25</h3>
                <p>SOH, DC RTE and auxiliary losses are inputs. Capacity, efficiency and yearwise settlement are calculated when the pipeline runs.</p>
              </div>
              <label className="battery-assumptions-toggle">
                <input
                  type="checkbox"
                  checked={assumptionsEnabled}
                  disabled={!isEditing}
                  onChange={(event) => setAssumptionsEnabled(event.target.checked)}
                />
                <span>{assumptionsEnabled ? 'Yearwise model active' : 'Use legacy model'}</span>
              </label>
            </div>

            {!assumptionsEnabled ? (
              <div className="battery-legacy-notice">
                This saved node will keep its existing battery calculation. Enable the yearwise model to add degradation and cycle-life settlement.
              </div>
            ) : (
              <>
                <div className="battery-assumption-scalars">
                  <label className="battery-modal-label">
                    Calendar retention (%)
                    <input className="battery-modal-input" type="number" step="any" value={calendarRetentionPct} onChange={(e) => setCalendarRetentionPct(e.target.value)} />
                  </label>
                  <label className="battery-modal-label">
                    AC efficiency at POI (%)
                    <input className="battery-modal-input" type="number" step="any" value={acEfficiencyPct} onChange={(e) => setAcEfficiencyPct(e.target.value)} />
                  </label>
                  <label className="battery-modal-label">
                    Auxiliary losses (MWh)
                    <input
                      className="battery-modal-input"
                      type="number"
                      min="0"
                      step="any"
                      value={auxiliaryLossMwh}
                      onChange={(e) => setAuxiliaryLossMwh(e.target.value)}
                    />
                    {legacyAuxiliaryLoadKw !== null && auxiliaryLossMwh === '' && (
                      <small className="battery-modal-help">
                        Legacy assumption: {legacyAuxiliaryLoadKw} kW. Enter an MWh value to replace it.
                      </small>
                    )}
                  </label>
                  <label className="battery-modal-label">
                    Cycles per day
                    <input className="battery-modal-input" type="number" step="any" value={cyclesPerDay} onChange={(e) => setCyclesPerDay(e.target.value)} />
                  </label>
                  <label className="battery-modal-label">
                    Maximum cycle life
                    <input className="battery-modal-input" type="number" step="any" value={maximumCycleLife} onChange={(e) => setMaximumCycleLife(e.target.value)} />
                  </label>
                </div>

                {profileIncreaseCount > 0 && (
                  <div className="battery-profile-warning">
                    {profileIncreaseCount} profile transition{profileIncreaseCount === 1 ? '' : 's'} increase SOH or DC RTE. The values will be accepted as entered.
                  </div>
                )}

                <div className="battery-profile-table-wrap">
                  <table className="battery-profile-table">
                    <thead>
                      <tr>
                        <th>Condition</th>
                        <th>SOH (%)</th>
                        <th>DC RTE (%)</th>
                        <th>Cumulative cycles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annualProfile.map((row, index) => (
                        <tr key={row.period} className={index === 0 ? 'sat-row' : ''}>
                          <th scope="row">{row.period === 'SAT' ? 'SAT baseline' : `Year ${row.period}`}</th>
                          <td>
                            <input
                              aria-label={`SOH for ${row.period}`}
                              type="number"
                              min="0"
                              max="100"
                              step="any"
                              value={row.soh_pct}
                              onChange={(event) => updateProfileCell(index, 'soh_pct', event.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              aria-label={`DC RTE for ${row.period}`}
                              type="number"
                              min="0"
                              max="100"
                              step="any"
                              value={row.dc_rte_pct}
                              onChange={(event) => updateProfileCell(index, 'dc_rte_pct', event.target.value)}
                            />
                          </td>
                          <td>{index === 0 ? '0' : `${Number(cyclesPerDay || 0) * 365 * index}`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <button
          className="battery-modal-submit"
          type="submit"
          disabled={isSubmitting || (!isEditing && !!nodeNameError) || !chargeTimeSlots.length || !dischargeTimeSlots.length}
        >
          {isSubmitting ? 'Saving...' : 'Save Battery Node'}
        </button>
      </form>
    </div>,
    document.body
  );
};

export default BatteryConfigModal;
